import { useMutation, UseMutationOptions, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';

import sdk from '@medipass/web-sdk';

export type Transaction = Record<string, any>;
type TransactionTokenType = 'account' | 'session';

export const SESSION_REQUEST_OPTS = {
  requestConfig: {
    tokenType: 'session',
  },
} as const;

export const transactionKeys = {
  all: ['transaction'] as const,
  details: () => [...transactionKeys.all, 'detail'] as const,
  detail: ({ transactionId, tokenType }: { transactionId: string; tokenType?: TransactionTokenType }) =>
    [...transactionKeys.details(), transactionId, { tokenType }] as const,
};

type SessionTransactionQueryKey = ReturnType<typeof transactionKeys.detail>;

export async function getSessionTransaction(transactionId?: string | null) {
  if (!transactionId) {
    throw new Error('Missing transactionId');
  }

  return (await sdk.transactions.getTransactionByTransactionId(
    transactionId,
    undefined,
    SESSION_REQUEST_OPTS,
  )) as Transaction;
}

export function useSessionTransactionQuery<T = Transaction>({
  transactionId,
  useQueryOpts,
}: {
  transactionId?: string | null;
  useQueryOpts?: Omit<UseQueryOptions<Transaction, Error, T, SessionTransactionQueryKey>, 'queryFn' | 'queryKey'>;
}) {
  const isEnabledFromProps = typeof useQueryOpts?.enabled === 'boolean' ? useQueryOpts.enabled : true;

  const transactionQuery = useQuery({
    retry: false,
    ...(useQueryOpts || {}),
    queryKey: transactionKeys.detail({ transactionId: transactionId || '', tokenType: 'session' }),
    queryFn: async () => getSessionTransaction(transactionId),
    enabled: isEnabledFromProps,
  });

  return { transactionQuery };
}

export function useApproveAndSubmitClaimMutation({
  claimId,
  transactionId,
  useMutationOptions,
}: {
  claimId?: string;
  transactionId?: string;
  useMutationOptions?: Omit<UseMutationOptions<Transaction, Error, void>, 'mutationFn'>;
}) {
  const queryClient = useQueryClient();
  const mutationKey = [
    ...transactionKeys.detail({ transactionId: transactionId || '', tokenType: 'session' }),
    'approve-and-submit-claim',
    claimId || '',
  ] as const;

  const approveAndSubmitClaimMutation = useMutation({
    ...(useMutationOptions || {}),
    mutationKey,
    mutationFn: async () => {
      if (!transactionId || !claimId) {
        throw new Error('Missing transaction or claim');
      }

      await sdk.transactions.approveClaim(transactionId, claimId, {}, SESSION_REQUEST_OPTS);
      return (await sdk.transactions.submitClaim(transactionId, claimId, {}, SESSION_REQUEST_OPTS)) as Transaction;
    },
    onSuccess: (data, variables, context, mutation) => {
      if (transactionId) {
        queryClient.setQueryData(transactionKeys.detail({ transactionId, tokenType: 'session' }), data);
      }
      if (useMutationOptions?.onSuccess) {
        useMutationOptions.onSuccess(data, variables, context, mutation);
      }
    },
  });

  const approveAndSubmitClaim = () => {
    if (queryClient.isMutating({ mutationKey }) > 0) return;

    approveAndSubmitClaimMutation.mutate();
  };

  return { approveAndSubmitClaim, approveAndSubmitClaimMutation };
}
